import { supabase } from '../lib/supabase';

interface CredentialDetails {
  api_key: string;
  is_active: boolean;
  instance_details?: any;
}

/**
 * Salva ou atualiza uma credencial de integração para o usuário autenticado.
 * O RLS garante que o user_id seja automaticamente associado ao usuário logado.
 * @param integrationName O nome da integração (e.g., 'serpapi', 'whatsapp').
 * @param details Os detalhes da credencial (incluindo api_key).
 */
export async function saveCredential(
  integrationName: string,
  details: CredentialDetails
): Promise<void> {

  // 1. Obter o ID do usuário logado
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado. Por favor, faça login novamente.');
  }

  // 2. Preparar os dados para upsert
  const dataToUpsert = {
    user_id: user.id, // Adicionando user_id explicitamente
    integration_name: integrationName,
    api_key: details.api_key,
    is_active: details.is_active,
    instance_details: details.instance_details,
  };

  // Tentativa de inserir ou atualizar (upsert)
  const { error } = await supabase
    .from('user_credentials')
    .upsert(
      dataToUpsert,
      {
        onConflict: 'user_id, integration_name', // Conflito na combinação user_id e integration_name
        ignoreDuplicates: false,
      }
    );

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error saving ${integrationName} credential:`, error);
    }
    throw new Error(`Falha ao salvar a configuração de ${integrationName}.`);
  }
}

/**
 * Remove uma credencial de integração do usuário autenticado.
 */
export async function deleteCredential(integrationName: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }

  const { error } = await supabase
    .from('user_credentials')
    .delete()
    .eq('integration_name', integrationName)
    .eq('user_id', user.id); // Garante que só deleta credenciais do próprio usuário

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error deleting ${integrationName} credential:`, error);
    }
    throw new Error(`Falha ao desconectar ${integrationName}.`);
  }
}

/**
 * Busca uma credencial de integração específica do usuário autenticado.
 */
export async function fetchCredential(integrationName: string): Promise<CredentialDetails | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_credentials')
    .select('api_key, is_active, instance_details')
    .eq('integration_name', integrationName)
    .eq('user_id', user.id); // Filtra explicitamente pelo usuário autenticado

  if (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error fetching ${integrationName} credential:`, error);
    }
    throw new Error(`Falha ao carregar status de ${integrationName}.`);
  }

  // Retorna o primeiro item se houver dados, caso contrário, null
  return data && data.length > 0 ? (data[0] as CredentialDetails) : null;
}